import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { DomainId } from "../content/domains";
import type { RoleId } from "../content/roles";
import { trackEvent } from "../lib/analytics";
import { type WaitlistSource, submitToSheets } from "../lib/sheets";
import { getCapturedUtm } from "./useUtmCapture";

export type WaitlistStatus = "idle" | "submitting" | "submitted" | "error";

export interface WaitlistSubmitInput {
  email: string;
  domains: DomainId[];
  domainOther?: string;
  role?: RoleId;
  source: WaitlistSource;
  honeypot?: string;
}

export interface WaitlistContextValue {
  status: WaitlistStatus;
  email: string;
  followupStatus: WaitlistStatus;
  error: string | null;
  submit: (input: WaitlistSubmitInput) => Promise<void>;
  submitFollowup: (painPoint: string) => Promise<void>;
  reset: () => void;
}

export const WaitlistContext = createContext<WaitlistContextValue | null>(null);

export function useWaitlistController(): WaitlistContextValue {
  const [status, setStatus] = useState<WaitlistStatus>("idle");
  const [followupStatus, setFollowupStatus] = useState<WaitlistStatus>("idle");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (input: WaitlistSubmitInput) => {
    setStatus("submitting");
    setError(null);
    try {
      const utm = getCapturedUtm();
      await submitToSheets({
        email: input.email,
        source: input.source,
        domains: input.domains.join(","),
        domainOther: input.domainOther,
        role: input.role || undefined,
        honeypot: input.honeypot,
        ...utm,
      });
      setEmail(input.email);
      setStatus("submitted");
      trackEvent("waitlist_submit", {
        domains: input.domains.join(","),
        role: input.role,
        source: input.source,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong.";
      setStatus("error");
      setError(message);
    }
  }, []);

  const submitFollowup = useCallback(
    async (painPoint: string) => {
      if (!email) return;
      setFollowupStatus("submitting");
      try {
        await submitToSheets({ email, source: "followup", painPoint });
        setFollowupStatus("submitted");
        trackEvent("followup_submit", { length: painPoint.length });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Something went wrong.";
        setFollowupStatus("error");
        setError(message);
      }
    },
    [email]
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setFollowupStatus("idle");
    setError(null);
    setEmail("");
  }, []);

  return useMemo(
    () => ({ status, email, followupStatus, error, submit, submitFollowup, reset }),
    [status, email, followupStatus, error, submit, submitFollowup, reset]
  );
}

export function useWaitlist(): WaitlistContextValue {
  const ctx = useContext(WaitlistContext);
  if (!ctx) {
    throw new Error("useWaitlist must be used inside <WaitlistProvider>");
  }
  return ctx;
}
